import React, { useMemo } from 'react';

import useChartData from 'hooks/Meteorology/Fields/TrackingForecast/DetailedView/useChartData';
import QueryErrorState from 'components/DesignSystem/QueryErrorState';
import Snackbar from 'components/DesignSystem/Snackbar';
import EChartsReact from 'components/ThemedECharts';
import CentralizedSpinner from 'components/CentralizedSpinner';
import HeatmapTable from 'components/Meteorology/TrackingForecast/DetailedView/HeatmapTable';
import heatmapMatrixFromOption from 'helper/Meteorology/TrackingForecast/DetailedView/heatmapMatrixFromOption';
import withScrollableLegend from 'helper/Meteorology/TrackingForecast/DetailedView/scrollableLegend';
import enhanceHeatmapOption, {
  isHeatmapOption,
  rowCount,
  heatmapHeight,
} from 'helper/Meteorology/Fields/TrackingForecast/DetailedView/enhanceHeatmapOption';

import './index.scss';

// Titulo do grafico vem pronto do backend (option.title) — nao ha como
// garantir que sempre caiba no espaco disponivel. Isso e so uma rede de
// seguranca de frontend (trunca text e subtext com reticencias, e reserva
// espaco no grid pra o bloco titulo+subtitulo nao sobrepor o eixo Y);
// largura/conteudo ideal do texto em si segue como conversa em aberto com
// o time de backend.
const TITLE_BLOCK_GRID_TOP = 90;

function withTitleOverflowSafety(option: any, titleMaxWidth: number) {
  if (!option?.title || Array.isArray(option.title)) return option;

  const title = {
    ...option.title,
    textStyle: {
      ...option.title.textStyle,
      overflow: 'truncate',
      width: titleMaxWidth,
    },
    ...(option.title.subtext && {
      subtextStyle: {
        ...option.title.subtextStyle,
        overflow: 'truncate',
        width: titleMaxWidth,
      },
    }),
  };

  const grid = option.grid && !Array.isArray(option.grid)
    ? { ...option.grid, top: TITLE_BLOCK_GRID_TOP }
    : option.grid;

  return {
    ...option,
    title,
    grid,
  };
}

export default function TFDVChart({
  titleMaxWidth,
}: {
  titleMaxWidth?: number;
}) {
  const {
    key,
    data,
    queryError,
    isFetching,
    autoQuerying,
  } = useChartData();

  const matrix = useMemo(() => heatmapMatrixFromOption(data), [data]);

  // `autoQuerying` cobre a cascata de abertura (#1822): as requisições em série
  // rodam antes da consulta final ter seu próprio isFetching, então sem isto o
  // gráfico mostraria a dica vazia sem spinner até a última requisição.
  if (isFetching || autoQuerying) {
    return (
      <div className="tracking-forecast-detailed-view-chart mt-2">
        <CentralizedSpinner />
      </div>
    );
  }

  if (queryError) {
    return (
      <div className="tracking-forecast-detailed-view-chart mt-3">
        <QueryErrorState title={queryError.data} message={queryError.description} />
      </div>
    );
  }

  const style = isHeatmapOption(data) && !matrix
    ? { height: heatmapHeight(rowCount(data)) }
    : undefined;

  // Gráfico (não-matriz): aplica o enhance de heatmap e a legenda rolável da
  // main e, por cima, a rede de segurança de overflow de título da
  // responsividade quando há `titleMaxWidth`. A matriz (freeze-panes) é
  // renderizada pela HeatmapTable.
  const enhancedOption = data && !matrix
    ? withScrollableLegend(enhanceHeatmapOption(data))
    : undefined;
  const chartOption = enhancedOption && titleMaxWidth
    ? withTitleOverflowSafety(enhancedOption, titleMaxWidth)
    : enhancedOption;

  return (
    <div className="tracking-forecast-detailed-view-chart mt-3" style={style}>
      {data && matrix && <HeatmapTable key={key} matrix={matrix} />}
      {chartOption && <EChartsReact key={key} option={chartOption} />}
      {!data && (
        <Snackbar className="text-center">Utilize o painel de controle para realizar uma consulta.</Snackbar>
      )}
    </div>
  );
}
